import React, { useCallback, useEffect, useState } from "react";
import { ArrowPathIcon, PhotoIcon, XMarkIcon, LinkIcon, CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { API_URL } from "../config/connect";
import { activateUserBanner, deactivateCurrentBanner, getBannerCandidates, getPublicBanner } from "../api/systemBannerAPI";

const resolveCandidates = (responseData) => {
  if (Array.isArray(responseData)) return responseData;
  return responseData?.items || responseData?.candidates || responseData?.data || [];
};

// `GET /api/v1/banner` енді `{ "banners": [...] }` тізімін қайтарады (біріктірілген:
// редакциялық MarketingPost + төлемді MainBannerSlot слоттары). Spec §4
// балама пішімдерін де қабылдаймыз (banners / banner массив / banner объект /
// жоғарғы деңгейдегі массив/объект) — frontend Second.js-пен бірдей логика.
const resolvePublicBanners = (responseData) => {
  if (!responseData) return [];
  const raw = responseData.banners ?? responseData.banner ?? responseData;
  const list = Array.isArray(raw) ? raw : raw && typeof raw === "object" ? [raw] : [];
  return list.map(normalizePublicBannerItem).filter(Boolean);
};

const normalizePublicBannerItem = (item) => {
  if (!item || typeof item !== "object") return null;
  const image_url = item.image_url || item.background_image || item.url || item.banner_url || null;
  // spec §6.4 — суретсіз банер көрсетуге жарамсыз, тізімнен өтпейді
  if (!image_url) return null;
  return {
    image_url,
    title: item.title || "",
    description: item.description || "",
    cta_link: item.cta_link || item.cta_url || null,
  };
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const status = error?.status;
  if (status === 401) return "Сессия истекла. Войдите в админ-панель заново.";
  if (status === 403) return "Недостаточно прав для управления баннерами.";
  if (status === 404) return "Endpoint не найден. Версия backend не поддерживает API управления баннерами.";
  if (status >= 500) return "Ошибка сервера при работе с баннерами. Повторите позже.";
  return error?.message || fallbackMessage;
};

// Относительный путь дополняем адресом backend (spec §3)
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (/^(https?:|data:)/.test(url)) return url;
  return `${API_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

function SystemBannerManagement() {
  const [candidates, setCandidates] = useState([]);
  const [publicBanners, setPublicBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingUserId, setProcessingUserId] = useState(null);
  const [deactivating, setDeactivating] = useState(false);
  const [toast, setToast] = useState(null);
  const [isApiIncompatible, setIsApiIncompatible] = useState(false);

  useEffect(() => {
    if (!toast || toast.type === "error") return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setIsApiIncompatible(false);
    try {
      setCandidates(resolveCandidates(await getBannerCandidates()));
      try {
        setPublicBanners(resolvePublicBanners(await getPublicBanner()));
      } catch (bannerError) {
        console.error("Error loading public banner:", bannerError);
        setPublicBanners([]);
      }
    } catch (fetchError) {
      console.error("Error loading system banner data:", fetchError);
      if (fetchError?.status === 404) setIsApiIncompatible(true);
      setToast({ type: "error", text: getApiErrorMessage(fetchError, "Не удалось загрузить данные баннера") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleActivateBanner = async (candidate) => {
    setProcessingUserId(candidate.user_id);
    setToast(null);
    try {
      await activateUserBanner(candidate.user_id);
      await fetchData();
      setToast({ type: "success", text: `Баннер «${candidate.company_name || `#${candidate.user_id}`}» теперь на главной.` });
    } catch (e) {
      setToast({ type: "error", text: getApiErrorMessage(e, "Не удалось активировать баннер") });
    } finally {
      setProcessingUserId(null);
    }
  };

  const handleDeactivateBanner = async () => {
    setDeactivating(true);
    setToast(null);
    try {
      await deactivateCurrentBanner();
      await fetchData();
      setToast({ type: "success", text: "Баннер снят с главной." });
    } catch (e) {
      setToast({ type: "error", text: getApiErrorMessage(e, "Не удалось деактивировать баннер") });
    } finally {
      setDeactivating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-text-secondary">
          Слайдер на главной сайта собирается из редакционного баннера («Маркетинг-посты» или бизнес-пользователь ниже) и оплаченных баннеров.
        </p>
        <button type="button" onClick={fetchData} disabled={loading} title="Обновить" aria-label="Обновить" className="shrink-0 rounded-lg border border-border-primary p-2 text-text-secondary hover:bg-bg-tertiary">
          <ArrowPathIcon className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {toast && (
        <div
          role={toast.type === "error" ? "alert" : "status"}
          className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${
            toast.type === "error" ? "border-rose-500/30 bg-rose-500/10 text-rose-500" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-500"
          }`}
        >
          <span>{toast.text}</span>
          <button type="button" onClick={() => setToast(null)} aria-label="Закрыть" className="shrink-0 opacity-70 hover:opacity-100">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Live slider */}
      <section>
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-primary">
          Сейчас на главной
          <span className="rounded-full bg-bg-tertiary px-2 text-xs tabular-nums text-text-muted">{publicBanners.length}</span>
        </h3>
        {publicBanners.length > 0 ? (
          <div className="flex snap-x gap-4 overflow-x-auto pb-2">
            {publicBanners.map((banner, index) => (
              <article key={`${banner.image_url}-${index}`} className="w-[22rem] shrink-0 snap-start overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm">
                <div className="relative aspect-[3/1] bg-bg-tertiary">
                  <img src={resolveImageUrl(banner.image_url)} alt={banner.title || ""} className="h-full w-full object-cover" />
                  <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-xs font-medium text-white">Слайд {index + 1}</span>
                </div>
                <div className="space-y-1 p-3 text-sm">
                  <p className="truncate font-semibold text-text-primary">{banner.title || "Без заголовка"}</p>
                  {banner.description && <p className="line-clamp-2 text-xs text-text-secondary">{banner.description}</p>}
                  {banner.cta_link && (
                    <a href={banner.cta_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 truncate text-xs text-[#7C3AED] hover:underline dark:text-indigo-300">
                      <LinkIcon className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{banner.cta_link}</span>
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="rounded-block border border-dashed border-border-primary bg-bg-card px-6 py-8 text-center text-sm text-text-secondary">
            {loading ? "Загрузка…" : "Активных баннеров нет — на главной только статичные слайды."}
          </p>
        )}
      </section>

      {/* Candidates */}
      <section>
        <h3 className="mb-1 text-sm font-semibold text-text-primary">Баннеры бизнес-пользователей</h3>
        <p className="mb-3 text-xs text-text-muted">Одобренный баннер компании можно вывести на главную как редакционный. Одновременно активен один.</p>

        {isApiIncompatible && (
          <p className="mb-3 flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-500">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
            Версия backend не поддерживает управление баннерами через админ-панель.
          </p>
        )}

        {loading && candidates.length === 0 ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="aspect-[3/2] animate-pulse rounded-block border border-border-primary bg-bg-card" />
            ))}
          </div>
        ) : candidates.length === 0 ? (
          <p className="rounded-block border border-dashed border-border-primary bg-bg-card px-6 py-8 text-center text-sm text-text-secondary">Нет баннеров пользователей.</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {candidates.map((candidate) => {
              const isActive = Boolean(candidate.is_active);
              const busy = processingUserId === candidate.user_id;
              return (
                <article
                  key={candidate.user_id}
                  className={`overflow-hidden rounded-block border bg-bg-card shadow-sm ${isActive ? "border-emerald-500/50 ring-1 ring-emerald-500/30" : "border-border-primary"}`}
                >
                  <div className="relative aspect-[3/1] bg-bg-tertiary">
                    {candidate.banner_url ? (
                      <img src={resolveImageUrl(candidate.banner_url)} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-text-muted">
                        <PhotoIcon className="h-8 w-8" />
                      </div>
                    )}
                    {isActive && (
                      <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-0.5 text-xs font-semibold text-white shadow">
                        <CheckCircleIcon className="h-3.5 w-3.5" /> На главной
                      </span>
                    )}
                  </div>
                  <div className="space-y-3 p-4">
                    <div>
                      <p className="truncate text-sm font-semibold text-text-primary">{candidate.company_name || "Без названия"}</p>
                      <p className="text-xs text-text-muted">
                        пользователь <span className="font-mono">#{candidate.user_id}</span>
                      </p>
                    </div>
                    {isActive ? (
                      <button
                        type="button"
                        disabled={deactivating || loading}
                        onClick={handleDeactivateBanner}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-rose-500/40 px-3 py-2 text-sm font-medium text-rose-500 hover:bg-rose-500/10 disabled:opacity-50"
                      >
                        {deactivating && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                        Снять с главной
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={Boolean(processingUserId) || !candidate.user_id || !candidate.banner_url}
                        onClick={() => handleActivateBanner(candidate)}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-accent-grad px-3 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                      >
                        {busy && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                        Показать на главной
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

export default SystemBannerManagement;
